import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, XCircle, Loader2, AlertTriangle, MapPin, FileWarning, RotateCcw } from 'lucide-react';

const ERROR_TYPE_LABELS = {
  geocoding: { label: 'Location Error', icon: MapPin, color: 'text-orange-600' },
  validation: { label: 'Data Error', icon: FileWarning, color: 'text-red-600' },
  calculation: { label: 'Calculation Error', icon: XCircle, color: 'text-red-600' },
};

export default function ProcessingView({ sessionId, totalRows, streamTicket, resultsLoading, onComplete, onError, onReset }) {
  const [processed, setProcessed] = useState(0);
  const [rowStatuses, setRowStatuses] = useState([]);
  const [error, setError] = useState(null);
  const eventSourceRef = useRef(null);
  // The parent passes new onComplete/onError functions on every render. With them in the effect's
  // dependencies, each re-render closed the stream and reopened it with the SAME single-use
  // ticket → 401 → "Connection lost" (seen right after a successful run). Keep the latest
  // callbacks in refs so the stream is opened exactly once per session/ticket.
  const onCompleteRef = useRef(onComplete);
  const onErrorRef = useRef(onError);
  onCompleteRef.current = onComplete;
  onErrorRef.current = onError;

  useEffect(() => {
    if (!sessionId) return;
    let finished = false;

    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
    const url = `${API}/agent/stream/${sessionId}?ticket=${encodeURIComponent(streamTicket)}`;

    const es = new EventSource(url);
    eventSourceRef.current = es;

    es.addEventListener('row_complete', (e) => {
      const data = JSON.parse(e.data);
      setProcessed(data.processed);
      setRowStatuses((prev) => [
        ...prev,
        {
          row_index: data.row_index,
          status: data.status,
          emissions: data.emissions,
          error: data.error,
          error_type: data.error_type,
        },
      ]);
    });

    es.addEventListener('complete', (e) => {
      const summary = JSON.parse(e.data);
      finished = true;
      es.close();
      onCompleteRef.current?.(summary);
    });

    es.addEventListener('error', (e) => {
      if (e.data) {
        const data = JSON.parse(e.data);
        finished = true;
        setError(data.error);
        onErrorRef.current?.(data.error);
      }
      es.close();
    });

    es.onerror = () => {
      if (!finished && es.readyState === EventSource.CLOSED) {
        setError('Connection lost. Results may still be available.');
      }
    };

    return () => {
      finished = true;
      es.close();
    };
  }, [sessionId, streamTicket]);

  const pct = totalRows > 0 ? Math.round((processed / totalRows) * 100) : 0;
  const successCount = rowStatuses.filter((r) => r.status === 'success' || r.status === 'warning').length;
  const errorCount = rowStatuses.filter((r) => r.status === 'error').length;

  // Categorize errors for the summary banner
  const errorSummary = useMemo(() => {
    const errors = rowStatuses.filter((r) => r.status === 'error');
    const byType = {};
    errors.forEach((r) => {
      const type = r.error_type || 'unknown';
      if (!byType[type]) byType[type] = [];
      byType[type].push(r);
    });
    return byType;
  }, [rowStatuses]);

  const getErrorIcon = (row) => {
    const cfg = ERROR_TYPE_LABELS[row.error_type];
    if (cfg) {
      const Icon = cfg.icon;
      return <Icon className={`w-3.5 h-3.5 ${cfg.color}`} />;
    }
    return <XCircle className="w-3.5 h-3.5 text-red-500" />;
  };

  return (
    <Card className="pro-card">
      <CardHeader className="pb-3 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
        <CardTitle className="text-lg font-semibold text-gray-800 flex items-center gap-2">
          {error ? (
            <XCircle className="w-5 h-5 text-red-500" />
          ) : resultsLoading ? (
            <Loader2 className="w-5 h-5 animate-spin text-sky-600" />
          ) : (
            <Loader2 className="w-5 h-5 animate-spin text-sky-600" />
          )}
          {error ? 'Processing Failed' : resultsLoading ? 'Loading results...' : `Processing ${totalRows} rows...`}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6 space-y-6">
        {/* Progress bar */}
        <div>
          <div className="flex justify-between text-sm mb-2">
            <span className="text-gray-600">
              {processed} of {totalRows} rows processed
            </span>
            <span className="font-semibold text-sky-600">{pct}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-sky-500 to-sky-600 rounded-full transition-all duration-300 ease-out"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-4 text-center">
          <div className="p-3 rounded-lg bg-gray-50">
            <p className="text-2xl font-bold text-gray-800">{processed}</p>
            <p className="text-xs text-gray-500">Processed</p>
          </div>
          <div className="p-3 rounded-lg bg-green-50">
            <p className="text-2xl font-bold text-green-600">{successCount}</p>
            <p className="text-xs text-gray-500">Successful</p>
          </div>
          <div className="p-3 rounded-lg bg-red-50">
            <p className="text-2xl font-bold text-red-600">{errorCount}</p>
            <p className="text-xs text-gray-500">Errors</p>
          </div>
        </div>

        {/* Error summary banners */}
        {Object.keys(errorSummary).length > 0 && (
          <div className="space-y-2">
            {errorSummary.geocoding?.length > 0 && (
              <div className="flex items-start gap-2 p-3 bg-orange-50 border border-orange-200 rounded-lg text-sm text-orange-800">
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  <strong>{errorSummary.geocoding.length} location error{errorSummary.geocoding.length > 1 ? 's' : ''}</strong>
                  {' '}— Could not geocode some addresses. Check spelling or ensure Google Maps API is configured.
                </span>
              </div>
            )}
            {errorSummary.validation?.length > 0 && (
              <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
                <FileWarning className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  <strong>{errorSummary.validation.length} data error{errorSummary.validation.length > 1 ? 's' : ''}</strong>
                  {' '}— Some rows have missing or invalid data (origin, destination, or weight).
                </span>
              </div>
            )}
          </div>
        )}

        {/* Recent activity log */}
        <div className="max-h-48 overflow-y-auto space-y-1">
          {rowStatuses.slice(-10).reverse().map((row, i) => (
            <div
              key={`${row.row_index}-${i}`}
              className="flex items-center gap-2 text-sm py-1 px-2 rounded"
            >
              {row.status === 'success' && <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />}
              {row.status === 'warning' && <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />}
              {row.status === 'error' && getErrorIcon(row)}
              <span className="text-gray-600">
                Row {row.row_index + 1}
                {row.status === 'success' && ` — ${row.emissions?.toFixed(4)} kgCO2e`}
                {row.status === 'warning' && ` — ${row.emissions?.toFixed(4)} kgCO2e (with warnings)`}
                {row.error && ` — ${row.error}`}
              </span>
            </div>
          ))}
        </div>

        {error && (
          <div className="space-y-3">
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
            <div className="flex justify-center gap-3">
              <Button
                variant="outline"
                onClick={onReset}
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Start Over
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
